import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, Alert, Label } from "@/components/ui";
import { UserPlus, Users, Mail, RotateCw, X, UserMinus, LogOut } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "family_member", label: "Family member", sub: "Full access — log meds, vitals, events" },
  { v: "aide", label: "Aide", sub: "Paid caregiver — shifts & sign-offs" },
  { v: "viewer", label: "Viewer", sub: "Read-only access to the circle" },
];

// An invite written before 2026-10-09 has no expiry because it had no token —
// no email was ever sent for it and it cannot be accepted. Say so plainly
// rather than leaving it looking like a healthy "pending" invite.
const isDead = (p) => !p.expires_at || new Date(p.expires_at) < new Date();

function CircleMember({ member: m, members, isMe, amPrimary, divider, onChanged }) {
  const [confirming, setConfirming] = useState(false);
  const [heir, setHeir] = useState("");
  const [busy, setBusy] = useState(false);

  const isPrimary = m.role === "primary_caregiver";
  // Leaving is always yours to do; removing someone else is the primary's.
  const canAct = isMe || amPrimary;
  // Stepping down means handing the circle over, because every record belongs
  // to it and it must never be left without a primary caregiver.
  const needsHeir = isPrimary && isMe;
  const others = members.filter((o) => o.user_id !== m.user_id);
  const soleMember = members.length === 1;

  const act = async () => {
    setBusy(true);
    try {
      await api.removeMember(m.user_id, needsHeir ? heir : undefined);
      toast.success(isMe ? "You've left the care circle" : `${m.name} was removed`);
      await onChanged();
    } catch (ex) {
      toast.error(ex?.message || "That didn't work");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className={`p-4 ${divider ? "border-t border-line" : ""}`} data-testid={`circle-member-${m.user_id}`}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-brand flex items-center justify-center text-white font-semibold shrink-0">
          {m.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-ink truncate">
            {m.name}{isMe && <span className="text-subtle font-normal"> (you)</span>}
          </div>
          <div className="text-xs text-subtle capitalize">{m.role.replace(/_/g, " ")}</div>
        </div>
        {canAct && !soleMember && !confirming && (
          <button type="button" data-testid={`remove-member-${m.user_id}`} onClick={() => setConfirming(true)}
            title={isMe ? "Leave this care circle" : `Remove ${m.name}`}
            className="p-2 rounded-lg text-subtle hover:bg-red-50 hover:text-red-600 transition-colors">
            {isMe ? <LogOut className="w-4 h-4" /> : <UserMinus className="w-4 h-4" />}
          </button>
        )}
      </div>

      {confirming && (
        <div className="mt-3 pl-12" data-testid={`confirm-remove-${m.user_id}`}>
          <p className="text-sm text-ink">
            {isMe
              ? "You'll lose access to this family's medications, appointments, readings and claims. Anything you logged stays part of their care record."
              : `${m.name} will lose access to everything in this circle, and we'll email to let them know. Anything they logged stays part of the care record.`}
          </p>
          {needsHeir && (
            <div className="mt-3">
              <Label>Hand the circle to</Label>
              <select value={heir} onChange={(e) => setHeir(e.target.value)} data-testid={`transfer-to-${m.user_id}`}
                className="mt-1 w-full p-2 rounded-lg border border-line bg-white text-sm text-ink">
                <option value="">Choose a member…</option>
                {others.map((o) => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
              </select>
              <p className="text-xs text-subtle mt-1">
                They become the primary caregiver. Every record belongs to the circle, so it can't be left without one.
              </p>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button onClick={act} loading={busy} disabled={needsHeir && !heir} variant="danger"
              data-testid={`confirm-remove-btn-${m.user_id}`}>
              {isMe ? "Leave circle" : "Remove"}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)} data-testid={`cancel-remove-${m.user_id}`}>
              Keep
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PendingInvite({ invite: p, onChanged }) {
  const [busy, setBusy] = useState(null);

  const run = async (what, fn, done) => {
    setBusy(what);
    try { await fn(); toast.success(done); await onChanged(); }
    catch (ex) { toast.error(ex?.message || "That didn't work"); }
    finally { setBusy(null); }
  };

  return (
    <div className="flex items-center gap-3 p-4 border-t border-line" data-testid={`pending-invite-${p.email}`}>
      <div className="w-9 h-9 rounded-full bg-brand-tint flex items-center justify-center shrink-0"><Mail className="w-4 h-4 text-brand-dark" /></div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-ink truncate">{p.email}</div>
        <div className="text-xs text-subtle">
          {isDead(p)
            ? <span className="text-amber-700">Never delivered — resend to make it live</span>
            : <>Invite pending · {p.role.replace(/_/g, " ")}</>}
        </div>
      </div>
      <button type="button" data-testid={`resend-invite-${p.email}`} disabled={!!busy}
        onClick={() => run("resend", () => api.resendInvite(p.email), "Invitation sent again")}
        title="Resend invitation"
        className="p-2 rounded-lg text-brand-dark hover:bg-brand-tint disabled:opacity-40 transition-colors">
        <RotateCw className={`w-4 h-4 ${busy === "resend" ? "animate-spin" : ""}`} />
      </button>
      <button type="button" data-testid={`revoke-invite-${p.email}`} disabled={!!busy}
        onClick={() => run("revoke", () => api.revokeInvite(p.email), "Invitation withdrawn")}
        title="Cancel invitation"
        className="p-2 rounded-lg text-subtle hover:bg-red-50 hover:text-red-600 disabled:opacity-40 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function Invite() {
  const { family, user, refresh } = useAuth();
  const myRole = (family?.members || []).find((m) => m.user_id === user?.id)?.role;
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("family_member");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const res = await api.invite(email.trim().toLowerCase(), role);
      setEmail("");
      await refresh();
      // Report what actually happened. This used to say "Invitation sent"
      // unconditionally, including when no email was sent at all.
      if (res?.status === "added") {
        toast.success(res?.emailed ? "Added to the circle — we let them know" : "Added to the circle");
      } else if (res?.emailed === false) {
        toast.warning("Invitation saved, but the email didn't go out. Try resending in a minute.");
      } else {
        toast.success("Invitation sent");
      }
    } catch (ex) { setErr(ex?.message || "Could not send invite"); }
    finally { setBusy(false); }
  };

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Your circle" title="Invite a member" />

      <Card>
        <form onSubmit={submit}>
          <Field label="Email" type="email" data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sibling@example.com" />
          <Label>Role</Label>
          <div className="space-y-2 mt-2 mb-4">
            {ROLES.map((r) => (
              <button key={r.v} type="button" data-testid={`invite-role-${r.v}`} onClick={() => setRole(r.v)}
                className={`w-full flex items-center gap-3 text-left p-3 rounded-lg border transition-colors ${role === r.v ? "border-brand bg-brand-tint" : "border-line hover:bg-brand-tint/50"}`}>
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${role === r.v ? "border-brand bg-brand" : "border-line"}`} />
                <div>
                  <div className="font-semibold text-sm text-ink">{r.label}</div>
                  <div className="text-xs text-subtle">{r.sub}</div>
                </div>
              </button>
            ))}
          </div>
          <Alert testid="invite-error">{err}</Alert>
          <Button type="submit" data-testid="invite-submit-button" loading={busy} disabled={!email.trim()} className="w-full">
            <UserPlus className="w-4 h-4" /> Send invitation
          </Button>
        </form>
      </Card>

      <h2 className="font-display text-xl text-ink mt-8 mb-3 flex items-center gap-2"><Users className="w-5 h-5 text-brand" /> Circle members</h2>
      <Card className="p-0 overflow-hidden">
        {(family?.members || []).map((m, i) => (
          <CircleMember
            key={m.user_id}
            member={m}
            members={family?.members || []}
            isMe={m.user_id === user?.id}
            amPrimary={myRole === "primary_caregiver"}
            divider={i > 0}
            onChanged={refresh}
          />
        ))}
        {(family?.pending_invites || []).map((p) => (
          <PendingInvite key={p.email} invite={p} onChanged={refresh} />
        ))}
      </Card>
    </div>
  );
}
